import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { ChangeEvent } from 'react';
import {
  AgentDto,
  ToolDto,
  useToolListControllerAgentTollListQuery,
} from '@shared/services/apiService/apiService';
import { ToolTypeEnum } from '@shared/constants/campaign.constants';
import {
  AGENT_IDLE_STATE_DELAY_MS_MAX,
  AGENT_IDLE_STATE_DELAY_MS_MIN,
  createDefaultIdleStateDraft,
  CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
  CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
  CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
  CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
  ExpandedAgentIdleState,
  ExpandedAgentIdleStateDraft,
  IdleStateValidationErrors,
  mapIdleStateToDraft,
} from '@features/Admin/Agent/model/idleState';
import { AgentToolsFormValue } from '@features/Admin/Agent/model/types';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { CampaignTextField } from '@shared/components/campaignTextField/campaignTextField';
import { MessageGroupEditor } from '@shared/components/MessageGroupEditor/MessageGroupEditor';
import { PhoneNumbersField } from '@shared/components/PhoneNumbersField';
import { StyledErrorText } from '@shared/components/errorText';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import {
  IconPhoneIncoming,
  IconPhoneOff,
  IconPhoneCall,
  IconMicrophone,
} from '@tabler/icons-react';
import { IconPostCall, IconWhatsapp } from '@shared/assets/images/svgs';
import AgentMcpToolsSection from './AgentMcpToolsSection';
import { GenderSettings } from '@shared/services/apiService/missing-types';
interface AgentAdvancedSettingsModalProps {
  open: boolean;
  onClose: () => void;
  agent: AgentDto;
  agents: AgentDto[];
  campaignTools?: ToolDto[];
  campaignName?: string;
  companyName?: string;
  formMethods: UseFormReturn<AgentToolsFormValue>;
  onSave: (idleState: ExpandedAgentIdleState | null) => Promise<boolean>;
  refetch: () => void;
}

const AgentAdvancedSettingsModal: React.FC<AgentAdvancedSettingsModalProps> = ({
  open,
  onClose,
  agent,
  agents,
  campaignTools = [],
  campaignName = '',
  companyName = '',
  formMethods,
  onSave,
  refetch,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [templateTextFieldValue, setTemplateTextFieldValue] = useState('');
  const [idleStateDraft, setIdleStateDraft] = useState<ExpandedAgentIdleStateDraft>(
    createDefaultIdleStateDraft(),
  );
  const [idleStateErrors, setIdleStateErrors] = useState<IdleStateValidationErrors>({});

  const {
    data: fetchedAgentTools,
    isLoading: isLoadingAgentTools,
    refetch: refetchAgentTools,
  } = useToolListControllerAgentTollListQuery(
    { id: agent.id || '' },
    { skip: !open || !agent.id, refetchOnMountOrArgChange: true },
  );

  const agentNonMcpTools = useMemo(() => {
    const tools = fetchedAgentTools || (agent.tools as ToolDto[]) || [];
    return tools.filter((tool) => tool.type !== ToolTypeEnum.Mcp);
  }, [fetchedAgentTools, agent.tools]);

  const allAgentTools = useMemo(() => {
    if (fetchedAgentTools) {
      return fetchedAgentTools;
    }
    return campaignTools;
  }, [fetchedAgentTools, campaignTools]);

  const {
    control,
    reset,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { errors },
    watch,
  } = formMethods;

  const formData = watch();

  // Initialize form with existing agent tools
  useEffect(() => {
    if (open && agent.id && !isLoadingAgentTools) {
      const callbackRequestTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.RequestCallback,
      );
      const voiceMailDetectionTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.VoiceMailDetection,
      );
      const endCallTool = agentNonMcpTools.find((tool) => tool.type === ToolTypeEnum.EndCall);
      const sendFollowUpMessageTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.WhatsappMessageSend,
      );
      const generateWhatsappMessageTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.GenerateWhatsappMessage,
      );
      const TransferTool = agentNonMcpTools.find((tool) => tool.type === ToolTypeEnum.TransferCall);

      const initialMessageTemplate = sendFollowUpMessageTool?.messageTemplate || '';
      setTemplateTextFieldValue(initialMessageTemplate);

      const callHandoffSignalTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.CallHandoffSignal,
      );
      const genderDetectionTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.GenderDetection,
      );
      const nextIdleStateDraft = mapIdleStateToDraft(agent.idleState as ExpandedAgentIdleState);

      reset({
        agentId: agent.id || '',
        callbackRequestTool: callbackRequestTool
          ? { ...callbackRequestTool, enabled: true }
          : { enabled: false, isAsync: true },
        voiceMailDetectionTool: voiceMailDetectionTool
          ? { ...voiceMailDetectionTool, enabled: true }
          : { enabled: false, isAsync: false },
        endCallTool: endCallTool
          ? { ...endCallTool, enabled: true }
          : { enabled: false, isAsync: false },
        sendFollowUpMessageTool: sendFollowUpMessageTool
          ? { ...sendFollowUpMessageTool, enabled: true, messageTemplate: initialMessageTemplate }
          : { enabled: false, messageTemplate: '', isAsync: true },
        generateWhatsappMessageTool: generateWhatsappMessageTool
          ? { ...generateWhatsappMessageTool, enabled: true }
          : { enabled: false, isAsync: true },
        TransferTool: TransferTool
          ? {
              ...TransferTool,
              enabled: true,
              phoneNumbers: (TransferTool as any).phoneNumbers || [],
            }
          : { enabled: false, phoneNumbers: [], isAsync: true },
        callHandoffSignalTool: callHandoffSignalTool
          ? { ...callHandoffSignalTool, enabled: true }
          : { enabled: false },
        genderDetectionTool: genderDetectionTool
          ? {
              ...genderDetectionTool,
              enabled: true,
              confidenceThreshold: (genderDetectionTool.genderSettings as GenderSettings)
                ?.confidenceThreshold,
              durationThreshold: (genderDetectionTool.genderSettings as GenderSettings)
                ?.durationThreshold,
              behaviorTemplate: (genderDetectionTool.genderSettings as GenderSettings)
                ?.behaviorTemplate,
            }
          : { enabled: false },
      });
      setIdleStateDraft(nextIdleStateDraft);
      setIdleStateErrors({});
    }
  }, [open, agent.id, agentNonMcpTools, reset, isLoadingAgentTools]);

  const validateTools = () => {
    clearErrors();
    const { sendFollowUpMessageTool } = formData;
    if (sendFollowUpMessageTool?.enabled && !sendFollowUpMessageTool.messageTemplate?.length) {
      setError('sendFollowUpMessageTool.messageTemplate', { type: 'required' });
    }
  };

  const updateIdleStateDraft = (patch: Partial<ExpandedAgentIdleStateDraft>) => {
    setIdleStateDraft((prev) => ({ ...prev, ...patch }));
    setIdleStateErrors((prev) => {
      const nextErrors = { ...prev };
      Object.keys(patch).forEach((key) => {
        delete nextErrors[key as keyof IdleStateValidationErrors];
      });
      return nextErrors;
    });
  };

  const getOptionalNumberError = (value: number, min: number, max: number, integerOnly = false) => {
    const isValidNumber = integerOnly ? Number.isInteger(value) : Number.isFinite(value);

    if (!isValidNumber || value < min || value > max) {
      return t('adminMainPage.agent.idleState.errors.optionalRange', { min, max });
    }

    return null;
  };

  const validateIdleState = (): ExpandedAgentIdleState | null => {
    const nextErrors: IdleStateValidationErrors = {};
    const delayMs = Number(idleStateDraft.delayMs);
    const customerReplyTimeoutSec = idleStateDraft.customerReplyTimeoutSec.trim()
      ? Number(idleStateDraft.customerReplyTimeoutSec)
      : null;
    const customerSilenceThresholdSec = idleStateDraft.customerSilenceThresholdSec.trim()
      ? Number(idleStateDraft.customerSilenceThresholdSec)
      : null;
    const customerMaxAttempts = idleStateDraft.customerMaxAttempts.trim()
      ? Number(idleStateDraft.customerMaxAttempts)
      : null;
    const messages = idleStateDraft.messages.map((message) => message.trim()).filter(Boolean);

    if (idleStateDraft.enabled) {
      if (
        !Number.isInteger(delayMs) ||
        delayMs < AGENT_IDLE_STATE_DELAY_MS_MIN ||
        delayMs > AGENT_IDLE_STATE_DELAY_MS_MAX
      ) {
        nextErrors.delayMs = t('adminMainPage.agent.idleState.errors.range', {
          min: AGENT_IDLE_STATE_DELAY_MS_MIN,
          max: AGENT_IDLE_STATE_DELAY_MS_MAX,
        });
      }

      if (!messages.length) {
        nextErrors.messages = t('adminMainPage.agent.idleState.errors.messagesRequired');
      }
    }

    if (customerReplyTimeoutSec !== null) {
      const error = getOptionalNumberError(
        customerReplyTimeoutSec,
        CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
        CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
      );

      if (error) {
        nextErrors.customerReplyTimeoutSec = error;
      }
    }

    if (customerSilenceThresholdSec !== null) {
      const error = getOptionalNumberError(
        customerSilenceThresholdSec,
        CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
        CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
      );

      if (error) {
        nextErrors.customerSilenceThresholdSec = error;
      }
    }

    if (customerMaxAttempts !== null) {
      const error = getOptionalNumberError(
        customerMaxAttempts,
        CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
        CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
        true,
      );

      if (error) {
        nextErrors.customerMaxAttempts = error;
      }
    }

    if (
      customerReplyTimeoutSec !== null &&
      customerSilenceThresholdSec !== null &&
      customerSilenceThresholdSec >= customerReplyTimeoutSec
    ) {
      nextErrors.customerSilenceThresholdSec = t(
        'adminMainPage.agent.idleState.errors.silenceThresholdRelation',
      );
    }

    setIdleStateErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return null;
    }

    return {
      enabled: idleStateDraft.enabled,
      delayMs: idleStateDraft.enabled ? delayMs : AGENT_IDLE_STATE_DELAY_MS_MIN,
      messages,
      customerReplyTimeoutSec,
      customerSilenceThresholdSec,
      customerMaxAttempts,
    };
  };

  const handleSave = async () => {
    const isValid = await trigger();
    validateTools();
    const validatedIdleState = validateIdleState();
    const errorFields = Object.keys(errors);
    if (!isValid || errorFields.length || !validatedIdleState) {
      return;
    }

    await onSave(validatedIdleState);
  };

  // Sync templateTextFieldValue with form value
  useEffect(() => {
    const currentTemplate = formData.sendFollowUpMessageTool?.messageTemplate || '';
    if (currentTemplate !== templateTextFieldValue) {
      setTemplateTextFieldValue(currentTemplate);
    }
  }, [formData.sendFollowUpMessageTool?.messageTemplate]);

  const mastecheValues = {
    agentName: agent.name || '',
    templateTextFieldValue:
      formData.sendFollowUpMessageTool?.messageTemplate || templateTextFieldValue,
    campaignName,
    companyName,
  };

  // console.log('formData', formData); // noisy: uncomment only when debugging

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {t('adminMainPage.operation.advancedSettings')} - {agent.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            padding: '20px 0',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '12px',
              'svg path': {
                stroke: theme.palette.getContrastText(theme.palette.background.default),
              },
            }}>
            <IconPostCall />
            <Typography fontSize="18px" fontWeight={600}>
              {t('campaignPage.postCall')}
            </Typography>
          </Box>

          <FormProvider {...formMethods}>
            <BorderedGrid
              sx={{
                paddingTop: '20px',
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconPhoneIncoming width={16} />
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('campaignPage.callbackRequestCheckbox')}
                  </Typography>
                </Stack>
                <FormGroup>
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                    }}
                    control={
                      <Controller
                        name={'callbackRequestTool.enabled'}
                        control={control}
                        render={({ field }) => {
                          return (
                            <CustomSwitch
                              {...field}
                              checked={Boolean(field.value)}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(e.target.checked);
                              }}
                            />
                          );
                        }}
                      />
                    }
                    label=""
                  />
                </FormGroup>
              </Stack>
            </BorderedGrid>

            <BorderedGrid sx={{ paddingTop: '20px' }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('adminMainPage.agent.idleState.title')}
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      sx={{ marginRight: 0 }}
                      control={
                        <CustomSwitch
                          checked={idleStateDraft.enabled}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            updateIdleStateDraft({ enabled: e.target.checked });
                          }}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>

                <CustomTextField
                  label={t('adminMainPage.agent.idleState.delayLabel')}
                  type="number"
                  fullWidth
                  disabled={!idleStateDraft.enabled}
                  inputProps={{
                    min: AGENT_IDLE_STATE_DELAY_MS_MIN,
                    max: AGENT_IDLE_STATE_DELAY_MS_MAX,
                    step: 1,
                  }}
                  value={idleStateDraft.delayMs}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateIdleStateDraft({ delayMs: event.target.value })
                  }
                />
                {idleStateErrors.delayMs && (
                  <StyledErrorText>{idleStateErrors.delayMs}</StyledErrorText>
                )}

                <MessageGroupEditor
                  groups={['idle']}
                  value={{ idle: idleStateDraft.messages }}
                  disabled={!idleStateDraft.enabled}
                  onChange={(value) => updateIdleStateDraft({ messages: value.idle ?? [] })}
                  labels={{
                    helperText: t('adminMainPage.agent.idleState.messagesHelper'),
                    emptyGroupText: t('adminMainPage.agent.idleState.messagesEmpty'),
                    addMessageLabel: t('adminMainPage.agent.idleState.addMessage'),
                    messagePlaceholder: t('adminMainPage.agent.idleState.messagePlaceholder'),
                    groupLabels: { idle: t('adminMainPage.agent.idleState.messagesGroup') },
                    deleteMessageAriaLabel: (_group, index) =>
                      t('adminMainPage.agent.idleState.deleteMessage', { index }),
                  }}
                />
                {idleStateErrors.messages && (
                  <StyledErrorText>{idleStateErrors.messages}</StyledErrorText>
                )}
              </Stack>
            </BorderedGrid>

            <BorderedGrid sx={{ paddingTop: '20px' }}>
              <Stack spacing={2}>
                <Typography fontSize="16px" fontWeight={600}>
                  {t('adminMainPage.agent.idleState.customerTitle')}
                </Typography>
                <Typography color="text.secondary" fontSize="14px">
                  {t('adminMainPage.agent.idleState.customerHelper')}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <CustomTextField
                      label={t('adminMainPage.agent.idleState.replyTimeoutLabel')}
                      type="number"
                      fullWidth
                      disabled={false}
                      inputProps={{
                        min: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
                        max: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
                        step: 0.1,
                      }}
                      value={idleStateDraft.customerReplyTimeoutSec}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateIdleStateDraft({ customerReplyTimeoutSec: event.target.value })
                      }
                    />
                    {idleStateErrors.customerReplyTimeoutSec && (
                      <StyledErrorText>{idleStateErrors.customerReplyTimeoutSec}</StyledErrorText>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <CustomTextField
                      label={t('adminMainPage.agent.idleState.silenceThresholdLabel')}
                      type="number"
                      fullWidth
                      disabled={false}
                      inputProps={{
                        min: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
                        max: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
                        step: 0.1,
                      }}
                      value={idleStateDraft.customerSilenceThresholdSec}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateIdleStateDraft({ customerSilenceThresholdSec: event.target.value })
                      }
                    />
                    {idleStateErrors.customerSilenceThresholdSec && (
                      <StyledErrorText>
                        {idleStateErrors.customerSilenceThresholdSec}
                      </StyledErrorText>
                    )}
                  </Box>
                </Box>

                <CustomTextField
                  label={t('adminMainPage.agent.idleState.maxAttemptsLabel')}
                  type="number"
                  fullWidth
                  disabled={false}
                  inputProps={{
                    min: CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
                    max: CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
                    step: 1,
                  }}
                  value={idleStateDraft.customerMaxAttempts}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateIdleStateDraft({ customerMaxAttempts: event.target.value })
                  }
                />
                {idleStateErrors.customerMaxAttempts && (
                  <StyledErrorText>{idleStateErrors.customerMaxAttempts}</StyledErrorText>
                )}
              </Stack>
            </BorderedGrid>

            <BorderedGrid
              sx={{
                paddingTop: '20px',
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconMicrophone width={16} />
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('campaignPage.voiceMailDetectionCheckbox')}
                  </Typography>
                </Stack>
                <FormGroup>
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                    }}
                    control={
                      <Controller
                        name={'voiceMailDetectionTool.enabled'}
                        control={control}
                        render={({ field }) => (
                          <CustomSwitch
                            {...field}
                            checked={Boolean(field.value)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              field.onChange(e.target.checked);
                            }}
                          />
                        )}
                      />
                    }
                    label=""
                  />
                </FormGroup>
              </Stack>
            </BorderedGrid>

            <BorderedGrid
              sx={{
                paddingTop: '20px',
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconPhoneOff width={16} />
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('campaignPage.endCallCheckbox')}
                  </Typography>
                </Stack>
                <FormGroup>
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                    }}
                    control={
                      <Controller
                        name={'endCallTool.enabled'}
                        control={control}
                        render={({ field }) => (
                          <CustomSwitch
                            {...field}
                            checked={Boolean(field.value)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              field.onChange(e.target.checked);
                            }}
                          />
                        )}
                      />
                    }
                    label=""
                  />
                </FormGroup>
              </Stack>
            </BorderedGrid>

            <BorderedGrid
              sx={{
                paddingTop: '20px',
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    'svg path': {
                      fill: theme.palette.getContrastText(theme.palette.background.default),
                    },
                  }}>
                  <IconWhatsapp />
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('campaignPage.whatsAppMessageCheckbox')}
                  </Typography>
                </Stack>
                <FormGroup>
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                    }}
                    control={
                      <Controller
                        name={'sendFollowUpMessageTool.enabled'}
                        control={control}
                        render={({ field }) => (
                          <CustomSwitch
                            {...field}
                            checked={Boolean(field.value)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              field.onChange(e.target.checked);
                            }}
                          />
                        )}
                      />
                    }
                    label=""
                  />
                </FormGroup>
              </Stack>
              <Box>
                <CampaignTextField
                  mastecheValues={mastecheValues}
                  setTemplateTextFieldValue={(value: React.SetStateAction<string>) => {
                    const newValue =
                      typeof value === 'function' ? value(templateTextFieldValue) : value;
                    setTemplateTextFieldValue(newValue);
                    setValue('sendFollowUpMessageTool.messageTemplate', newValue);
                  }}
                  isCampaignActive={'inactive' as any}
                  disabled={false}
                />
              </Box>
              {errors.sendFollowUpMessageTool?.messageTemplate && (
                <StyledErrorText>{t('adminMainPage.errors.fillTheField')}</StyledErrorText>
              )}
            </BorderedGrid>

            <BorderedGrid
              sx={{
                paddingTop: '20px',
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    'svg path': {
                      fill: theme.palette.getContrastText(theme.palette.background.default),
                    },
                  }}>
                  <IconWhatsapp />
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('campaignPage.generateWhatsappMessageCheckbox')}
                  </Typography>
                </Stack>
                <FormGroup>
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                    }}
                    control={
                      <Controller
                        name={'generateWhatsappMessageTool.enabled'}
                        control={control}
                        render={({ field }) => (
                          <CustomSwitch
                            {...field}
                            checked={Boolean(field.value)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              field.onChange(e.target.checked);
                            }}
                          />
                        )}
                      />
                    }
                    label=""
                  />
                </FormGroup>
              </Stack>
            </BorderedGrid>

            <BorderedGrid
              sx={{
                paddingTop: '20px',
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    'svg path': {
                      stroke: theme.palette.getContrastText(theme.palette.background.default),
                    },
                  }}>
                  <IconPhoneCall width={16} />
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('campaignPage.transferCallCheckbox')}
                  </Typography>
                </Stack>
                <FormGroup>
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                    }}
                    control={
                      <Controller
                        name={'TransferTool.enabled'}
                        control={control}
                        render={({ field }) => (
                          <CustomSwitch
                            {...field}
                            checked={Boolean(field.value)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              field.onChange(e.target.checked);
                              if (!e.target.checked) {
                                setValue('TransferTool.phoneNumbers', []);
                              }
                            }}
                          />
                        )}
                      />
                    }
                    label=""
                  />
                </FormGroup>
              </Stack>
              <Box sx={{ mt: 2 }}>
                <Controller
                  name={'TransferTool.phoneNumbers'}
                  control={control}
                  render={({ field }) => (
                    <PhoneNumbersField
                      isTransfer
                      phoneNumbers={field.value || []}
                      onChange={(phoneNumbers) => {
                        field.onChange(phoneNumbers);
                        if (phoneNumbers.length > 0) {
                          setValue('TransferTool.enabled', true);
                        } else {
                          setValue('TransferTool.enabled', false);
                        }
                      }}
                      disabled={false}
                    />
                  )}
                />
              </Box>
            </BorderedGrid>

            <BorderedGrid sx={{ paddingTop: '20px' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    'svg path': {
                      stroke: theme.palette.getContrastText(theme.palette.background.default),
                    },
                  }}>
                  <IconPhoneCall width={16} />
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('campaignPage.callHandoffSignalCheckbox')}
                  </Typography>
                </Stack>
                <FormGroup>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    control={
                      <Controller
                        name={'callHandoffSignalTool.enabled'}
                        control={control}
                        render={({ field }) => (
                          <CustomSwitch
                            {...field}
                            checked={Boolean(field.value)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              field.onChange(e.target.checked);
                            }}
                          />
                        )}
                      />
                    }
                    label=""
                  />
                </FormGroup>
              </Stack>
            </BorderedGrid>

            <BorderedGrid sx={{ paddingTop: '20px' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    'svg path': {
                      stroke: theme.palette.getContrastText(theme.palette.background.default),
                    },
                  }}>
                  <IconMicrophone width={16} />
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('campaignPage.genderDetectionCheckbox')}
                  </Typography>
                </Stack>
                <FormGroup>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    control={
                      <Controller
                        name={'genderDetectionTool.enabled'}
                        control={control}
                        render={({ field }) => (
                          <CustomSwitch
                            {...field}
                            checked={Boolean(field.value)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              field.onChange(e.target.checked);
                            }}
                          />
                        )}
                      />
                    }
                    label=""
                  />
                </FormGroup>
              </Stack>
              <Controller
                name={'genderDetectionTool.enabled'}
                control={control}
                render={({ field: enabledField }) => (
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Controller
                        name={'genderDetectionTool.confidenceThreshold'}
                        control={control}
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            label={t('campaignPage.genderDetection.confidenceThreshold')}
                            type="number"
                            inputProps={{ step: 0.01, min: 0, max: 1 }}
                            fullWidth
                            disabled={!enabledField.value}
                            value={field.value ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              field.onChange(
                                e.target.value === '' ? undefined : parseFloat(e.target.value),
                              );
                            }}
                          />
                        )}
                      />
                      <Controller
                        name={'genderDetectionTool.durationThreshold'}
                        control={control}
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            label={t('campaignPage.genderDetection.durationThreshold')}
                            type="number"
                            inputProps={{ step: 1, min: 0 }}
                            fullWidth
                            disabled={!enabledField.value}
                            value={field.value ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              field.onChange(
                                e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                              );
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Controller
                      name={'genderDetectionTool.behaviorTemplate'}
                      control={control}
                      render={({ field }) => (
                        <CustomTextField
                          {...field}
                          label={t('campaignPage.genderDetection.behaviorTemplate')}
                          fullWidth
                          multiline
                          rows={3}
                          disabled={!enabledField.value}
                          value={field.value ?? ''}
                        />
                      )}
                    />
                  </Box>
                )}
              />
            </BorderedGrid>
          </FormProvider>

          <AgentMcpToolsSection
            agentId={agent.id || ''}
            agents={agents}
            tools={allAgentTools}
            refetch={() => {
              refetchAgentTools();
              refetch();
            }}
            disabled={false}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('adminMainPage.operation.cancel')}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t('adminMainPage.operation.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentAdvancedSettingsModal;
